// ============================================================================
// chatFileAttachment — строка файла в сообщении: имя/размер всегда видны,
// длинные имена обрезаются (truncate), пузырь не распирается.
// Markdown-вложение дополнительно показывает ОБРЕЗАННОЕ превью (600 символов),
// полный текст — существующий FilePreviewModal по клику.
// ============================================================================

import { useEffect, useState } from 'react';
import { FileText } from 'lucide-react';
import { getSDK } from '@/store/sdkStore';
import { fileBytes } from '@/lib/fileBytes';
import { logSwallowed } from '@/lib/errors';
import { formatBytes } from '@/components/chat/chatInfoData';
import { MarkdownView } from '@/components/viewer/FileViewer';

export interface ChatFileRef {
  id: string;
  name: string;
  size: number;
}

const MD_EXCERPT_MAX_BYTES = 8192;
const MD_EXCERPT_MAX_CHARS = 600;

export function ChatFileAttachment({ file, mine, onOpen, onDownload }: {
  file: ChatFileRef;
  mine: boolean;
  /** Полный просмотр (Markdown/предпросмотр файла) — FilePreviewModal по id. */
  onOpen: (fileId: string, fileName: string) => void;
  /** Скачивание (для обычных файлов). */
  onDownload: (fileId: string, fileName: string) => void;
}) {
  const isMarkdown = /\.md$/i.test(file.name);
  const [excerpt, setExcerpt] = useState('');

  useEffect(() => {
    if (!isMarkdown) {
      setExcerpt('');
      return;
    }
    let alive = true;
    setExcerpt('');
    getSDK().files.downloadFile({ id: file.id })
      .then((r) => {
        let text = '';
        try {
          const head = fileBytes(r.data).subarray(0, MD_EXCERPT_MAX_BYTES);
          text = new TextDecoder().decode(head).slice(0, MD_EXCERPT_MAX_CHARS).trim();
        } catch (e: unknown) {
          logSwallowed(e, 'components/chat/chatFileAttachment.tsx:decode');
        }
        if (alive) setExcerpt(text);
      })
      .catch((e: unknown) => {
        logSwallowed(e, 'components/chat/chatFileAttachment.tsx:download');
      });
    return () => { alive = false; };
  }, [file.id, isMarkdown]);

  return (
    <div className="min-w-0 max-w-full">
      <button
        type="button"
        className={`flex w-full min-w-0 items-center gap-1.5 rounded-xl border px-2 py-1.5 text-left text-[11px] hover:bg-black/5 ${mine ? 'border-primary/30' : 'border-muted-foreground/20'}`}
        onClick={() => (isMarkdown ? onOpen(file.id, file.name) : onDownload(file.id, file.name))}
        title={file.name}
      >
        <FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        <span className="min-w-0 flex-1 truncate">{file.name}</span>
        {file.size > 0 && <span className="shrink-0 text-[10px] opacity-60">{formatBytes(file.size)}</span>}
        {isMarkdown && <span className="shrink-0 text-[10px] opacity-70">Читать полностью</span>}
      </button>
      {isMarkdown && excerpt && (
        <div
          className="mt-1 max-h-48 cursor-pointer overflow-hidden rounded-lg border bg-muted/20 p-2 [overflow-wrap:anywhere]"
          onClick={() => onOpen(file.id, file.name)}
          title="Читать полностью"
        >
          <MarkdownView text={`${excerpt}\n\n…`} />
        </div>
      )}
    </div>
  );
}
